import { useMemo } from 'react';
import { groupOoxmlChanges } from '../../../core/lib/diffs';
import type { ChangesStatus, MessageWithChanges } from '../types/changesTypes';

export interface UseMessageChangesResult {
  hasOoxmlChanges: boolean;
  ooxmlChanges: string;
  effectiveChangesStatus: ChangesStatus;
  changesCount: number;
  shouldShowChangesPreview: boolean;
}

export const useMessageChanges = (message: MessageWithChanges): UseMessageChangesResult => {
  const hasOoxmlChanges = message.changes && typeof message.changes === 'string' && message.changes.trim().length > 0;
  const ooxmlChanges = hasOoxmlChanges ? message.changes || '' : '';

  // Determinar el estado efectivo de los cambios
  const effectiveChangesStatus: ChangesStatus = message.changesStatus || null;

  // Calcular el número de cambios
  const changesCount = useMemo(() => {
    if (!hasOoxmlChanges || !ooxmlChanges) return 0;
    try {
      const groups = groupOoxmlChanges(ooxmlChanges);
      return groups.length;
    } catch {
      return 0;
    }
  }, [hasOoxmlChanges, ooxmlChanges]);

  // Determinar si se debe mostrar el preview de cambios
  // Mostrar cuando hay cambios y no están rechazados/cancelados
  const shouldShowChangesPreview = hasOoxmlChanges && 
    effectiveChangesStatus !== 'rejected' && 
    effectiveChangesStatus !== 'cancelled';

  // Debug log
  if (hasOoxmlChanges) {
    console.log('🔍 [useMessageChanges] Estado completo:', {
      messageId: message.id,
      rawChangesStatus: message.changesStatus,
      effectiveChangesStatus,
      hasOoxmlChanges,
      ooxmlChangesLength: ooxmlChanges?.length || 0,
      timestamp: message.timestamp,
      timeSinceSent: message.timestamp ? (new Date().getTime() - new Date(message.timestamp).getTime()) : null,
      changesCount,
      shouldShowChangesPreview
    });
  }

  return {
    hasOoxmlChanges,
    ooxmlChanges,
    effectiveChangesStatus,
    changesCount,
    shouldShowChangesPreview
  };
};

